import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { CheckCircle2, Clock, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { useTranslation } from "react-i18next";

const COURTHOUSE = "https://images.unsplash.com/photo-1436450412740-6b988f486c6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjb3VydGhvdXNlJTIwY29sdW1ucyUyMGFyY2hpdGVjdHVyZSUyMGxhd3xlbnwwfHx8fDE3ODcyNDIxNTl8MA&ixlib=rb-4.1.0&q=85";

export default function Contact() {
  const { t } = useTranslation();
  const { profile } = useOutletContext() || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t("contact.errName");
    if (!form.email.trim()) errs.email = t("contact.errEmail");
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t("contact.errEmailInvalid");
    if (!form.message.trim()) errs.message = t("contact.errMessage");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/public/inquiries", { ...form, type: "contact" });
      setSent(true);
      toast.success(t("contact.toast"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-navy text-ivory hero-grain relative py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">{t("contact.kicker")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-semibold tracking-tight">{t("contact.title")}</h1>
          <p className="mt-5 text-ivory/70 max-w-2xl leading-relaxed">
            {t("contact.intro")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2 space-y-8">
          <div className="relative">
            <img src={COURTHOUSE} alt="Courthouse" className="w-full h-64 object-cover" />
            <div className="absolute inset-0 bg-navy/30" />
          </div>
          <div className="space-y-5" data-testid="contact-info">
            {[
              [MapPin, t("contact.office"), profile?.office_address],
              [Phone, t("contact.telephone"), profile?.phone],
              [Mail, t("contact.email"), profile?.email],
              [Clock, t("contact.hours"), profile?.office_hours],
            ].map(([Icon, label, value]) => (
              <div key={label} className="flex gap-4 bg-white border border-border p-5">
                <Icon className="h-5 w-5 text-gold-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
                  <p className="text-sm text-navy mt-1">{value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-gold-100 border-l-4 border-gold p-5 text-sm text-navy-700 leading-relaxed">
            {t("contact.disclaimer")}
          </div>
        </div>

        <div className="lg:col-span-3">
          {sent ? (
            <div className="bg-white border border-border p-12 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-gold-700 mx-auto mb-5" />
              <h2 className="font-serif text-3xl font-semibold text-navy">{t("contact.received")}</h2>
              <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                {t("contact.thanks", { name: form.name.split(" ")[0] })}
              </p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", message: "" }); }}
                data-testid="contact-send-another"
                className="mt-8 text-xs font-semibold uppercase tracking-wider text-gold-700 hover:text-navy transition-colors">
                {t("contact.another")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-border p-8 sm:p-12" data-testid="contact-form" noValidate>
              <h2 className="font-serif text-3xl font-semibold text-navy mb-10">{t("contact.sendMessage")}</h2>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label={t("contact.fullName")} error={errors.name} htmlFor="contact-name">
                  <input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={set("name")}
                    placeholder="Jane A. Smith"
                    className="bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm placeholder:text-muted-foreground/70" />
                </Field>
                <Field label={t("contact.emailAddress")} error={errors.email} htmlFor="contact-email">
                  <input id="contact-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")}
                    placeholder="jane@example.com"
                    className="bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm placeholder:text-muted-foreground/70" />
                </Field>
                <Field label={t("contact.phoneOptional")} htmlFor="contact-phone">
                  <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")}
                    placeholder="+1 (312) 555-0100"
                    className="bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm placeholder:text-muted-foreground/70" />
                </Field>
              </div>
              <div className="mt-8">
                <Field label={t("contact.howHelp")} error={errors.message} htmlFor="contact-message">
                  <textarea id="contact-message" data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")}
                    placeholder={t("contact.placeholderMatter")}
                    className="bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm placeholder:text-muted-foreground/70 resize-none" />
                </Field>
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                className="mt-10 bg-navy text-ivory hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-10 py-4">
                {sending ? t("contact.sending") : t("contact.submit")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
